import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

const CART_STORAGE_KEY = 'mad_donuts_cart';
const CART_DATE_KEY = 'mad_donuts_cart_date';
const CART_EXPIRY_DAYS = 3;

// Days of week for availability checking
const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Parse a YYYY-MM-DD string to a local-noon Date (timezone-safe, no UTC shift).
const parseDateOnly = (s) => {
  if (!s) return null;
  return new Date(`${s}T12:00:00`);
};

// Format a Date to a YYYY-MM-DD string using LOCAL parts (never toISOString,
// which shifts the calendar day in timezones east of UTC).
const toDateOnly = (d) => {
  if (!d) return null;
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${day}`;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedDeliveryDate, setSelectedDeliveryDateState] = useState(null);
  // The set of product ids resolved as available for the currently selected
  // delivery date (server-resolved: weekly rules + published menu). null = unknown.
  const [availableProductIds, setAvailableProductIds] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    loadCart();
    const savedDate = localStorage.getItem(CART_DATE_KEY);
    if (savedDate) {
      const d = parseDateOnly(savedDate);
      if (d && !Number.isNaN(d.getTime())) setSelectedDeliveryDateState(d);
    }
  }, []);

  // Persist the selected delivery date so it survives Shop → Checkout → refresh.
  const setSelectedDeliveryDate = (date) => {
    setSelectedDeliveryDateState(date);
    if (date) {
      localStorage.setItem(CART_DATE_KEY, toDateOnly(date));
    } else {
      localStorage.removeItem(CART_DATE_KEY);
    }
  };

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cart.length > 0) {
      saveCart(cart);
    }
  }, [cart]);

  const loadCart = () => {
    try {
      const savedData = localStorage.getItem(CART_STORAGE_KEY);
      if (savedData) {
        const { cart: savedCart, expiry } = JSON.parse(savedData);
        
        // Check if cart has expired
        if (expiry && new Date().getTime() > expiry) {
          localStorage.removeItem(CART_STORAGE_KEY);
          setCart([]);
          return;
        }
        
        if (Array.isArray(savedCart)) {
          setCart(savedCart);
        }
      }
    } catch (e) {
      console.error('Error loading cart:', e);
      setCart([]);
    }
  };

  const saveCart = (cartData) => {
    try {
      const expiry = new Date().getTime() + (CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        cart: cartData,
        expiry
      }));
    } catch (e) {
      console.error('Error saving cart:', e);
    }
  };

  const addToCart = (product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.id === productId) {
          const newQuantity = item.quantity + delta;
          if (newQuantity <= 0) return null;
          return { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(Boolean);
    });
  };

  const setItemQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.id === productId) {
          return { ...item, quantity };
        }
        return item;
      });
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => {
      // Use your_price (discounted) if available, otherwise use wholesale_price
      const price = item.your_price || item.wholesale_price;
      return sum + (price * item.quantity);
    }, 0);
  };

  const getCartSavings = () => {
    // Calculate total savings from discounts
    return cart.reduce((sum, item) => {
      if (item.has_discount && item.discount_amount) {
        return sum + (item.discount_amount * item.quantity);
      }
      return sum;
    }, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const openCart = () => setCartOpen(true);
  const closeCart = () => setCartOpen(false);
  const toggleCart = () => setCartOpen(prev => !prev);

  // Check if a product is available for a specific delivery date.
  // When the server-resolved availableProductIds set is known (a date has been
  // selected and products loaded), membership in that set is authoritative —
  // it already accounts for published menus (additive/exclusive). Otherwise we
  // fall back to the product's weekly day-of-week rule.
  const isProductAvailableForDate = (product, date) => {
    if (Array.isArray(availableProductIds)) {
      return availableProductIds.includes(product.id);
    }
    if (!date) return true; // No date selected = assume available
    const availableDays = product.available_days || [];
    if (availableDays.length === 0) return true; // Empty = all days
    const dayOfWeek = DAYS_OF_WEEK[date.getDay()];
    return availableDays.includes(dayOfWeek);
  };

  // Remove any cart items that aren't available for the freshly-resolved date.
  // Returns the list of removed items so the caller can warn the user.
  const revalidateCart = (availableIds) => {
    const idSet = new Set(availableIds || []);
    const removed = cart.filter((item) => !idSet.has(item.id));
    if (removed.length > 0) {
      setCart((prev) => prev.filter((item) => idSet.has(item.id)));
    }
    return removed;
  };

  // Get items unavailable for the selected delivery date
  const getUnavailableItems = () => {
    if (!selectedDeliveryDate) return [];
    return cart.filter(item => !isProductAvailableForDate(item, selectedDeliveryDate));
  };

  // Check if cart has any unavailable items
  const hasUnavailableItems = () => {
    return getUnavailableItems().length > 0;
  };

  // Get day name from date
  const getDayName = (date) => {
    if (!date) return '';
    return DAYS_OF_WEEK[date.getDay()];
  };

  // Format available days for display
  const formatAvailableDays = (availableDays) => {
    if (!availableDays || availableDays.length === 0) return 'All Days';
    if (availableDays.length === 7) return 'All Days';
    
    const shortNames = {
      'monday': 'Mon',
      'tuesday': 'Tue', 
      'wednesday': 'Wed',
      'thursday': 'Thu',
      'friday': 'Fri',
      'saturday': 'Sat',
      'sunday': 'Sun'
    };
    
    return availableDays.map(d => shortNames[d] || d).join(', ');
  };

  return (
    <CartContext.Provider value={{
      cart,
      cartOpen,
      selectedDeliveryDate,
      setSelectedDeliveryDate,
      availableProductIds,
      setAvailableProductIds,
      revalidateCart,
      addToCart,
      updateQuantity,
      setItemQuantity,
      removeFromCart,
      clearCart,
      getCartTotal,
      getCartSavings,
      getCartItemCount,
      openCart,
      closeCart,
      toggleCart,
      // Availability functions
      isProductAvailableForDate,
      getUnavailableItems,
      hasUnavailableItems,
      getDayName,
      formatAvailableDays
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
